// useSpeechRecognition: thin React wrapper around the Web Speech API
// (window.SpeechRecognition / webkitSpeechRecognition). Used by the
// Ask Siggy composer's mic button.
//
// Browser support: Chrome desktop + Android, iOS Safari (14.5+), and
// Edge. Firefox does not implement it. The hook exposes `supported`
// so the mic button can be hidden gracefully.
//
// PHI hygiene: speech recognition happens in-browser via the OS speech
// service (Apple / Google native). The recognized transcript only
// becomes PHI when it lands in the textarea and the user sends it to
// /api/chat, at which point the existing PHI flow takes over.

import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechRecognitionResult {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: ArrayLike<ArrayLike<SpeechRecognitionResult> & SpeechRecognitionResult>;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((this: SpeechRecognitionLike, ev: SpeechRecognitionEvent) => unknown) | null;
  onerror: ((this: SpeechRecognitionLike, ev: SpeechRecognitionErrorEvent) => unknown) | null;
  onend: ((this: SpeechRecognitionLike, ev: Event) => unknown) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: { new (): SpeechRecognitionLike };
    webkitSpeechRecognition?: { new (): SpeechRecognitionLike };
  }
}

type RecognitionState = 'idle' | 'listening' | 'error';

export interface SpeechRecognitionHandle {
  state: RecognitionState;
  error: string | null;
  supported: boolean;
  /** Start listening. The onTranscript callback fires with each interim + final chunk. */
  start: (onTranscript: (text: string, isFinal: boolean) => void) => void;
  stop: () => void;
}

function getCtor(): { new (): SpeechRecognitionLike } | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export function useSpeechRecognition(): SpeechRecognitionHandle {
  const [state, setState] = useState<RecognitionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const supported = getCtor() !== null;

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setState('idle');
  }, []);

  const start = useCallback((onTranscript: (text: string, isFinal: boolean) => void) => {
    const Ctor = getCtor();
    if (!Ctor) {
      setError('Speech recognition not supported on this browser');
      setState('error');
      return;
    }
    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (ev) => {
      let final = '';
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (!res) continue;
        const alt = res[0];
        if (!alt) continue;
        if (res.isFinal) final += alt.transcript;
        else interim += alt.transcript;
      }
      if (interim) onTranscript(interim, false);
      if (final) onTranscript(final, true);
    };
    recognition.onerror = (ev) => {
      setError(ev.error || 'recognition error');
      setState('error');
    };
    recognition.onend = () => {
      setState('idle');
    };
    recognitionRef.current = recognition;
    setError(null);
    setState('listening');
    try {
      recognition.start();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, []);

  return { state, error, supported, start, stop };
}
